import { useEffect, useMemo, useState } from "react";
import api, { money, apiErr, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Boxes, Download, AlertTriangle, Image as ImageIcon, Upload, X } from "lucide-react";
import { Pagination } from "@/components/Pagination";
import LeadDrawer from "@/components/LeadDrawer";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const hasClaim = (r) => !!(r.damage_status || r.damage_claim || (r.damage_photos || []).length || Number(r.damage_amount) > 0);

const LOAD_TYPES = ["", "FTL", "PTL", "LCV"];

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
};

const StatusPill = ({ status }) => {
  const map = {
    pending: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
    contacted: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
    quoted: "bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-400",
    confirmed: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
    assigned: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400",
    done: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
  };
  return <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold uppercase ${map[status] || "bg-muted text-muted-foreground"}`}>{status || "—"}</span>;
};

const DAMAGE_STATUSES = [
  { key: "open", label: "Open" },
  { key: "in_review", label: "In-Review" },
  { key: "settled", label: "Settled" },
];
const DamageBadge = ({ status }) => {
  const map = {
    open: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    in_review: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    settled: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  };
  const label = { open: "Open", in_review: "In-Review", settled: "Settled" };
  return <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-bold uppercase ${map[status] || "bg-muted text-muted-foreground"}`}>{label[status] || status}</span>;
};

const StageBadge = ({ stage }) => (
  <span className={`inline-flex px-1.5 py-0.5 text-[9px] font-bold uppercase ${stage === "packing" ? "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"}`}>{stage === "packing" ? "Packing" : "Delivered"}</span>
);

function RemarksCell({ row, onSave }) {
  const [val, setVal] = useState(row.remarks || "");
  useEffect(() => { setVal(row.remarks || ""); }, [row.remarks]);
  return (
    <Textarea
      rows={2}
      className="rounded-none bg-background text-xs min-w-[180px]"
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => { if ((val || "") !== (row.remarks || "")) onSave(val); }}
      placeholder="Add remarks…"
      data-testid={`ilot-remarks-input-${row.order_number}`}
    />
  );
}

export default function ILOTPage({
  stage = "delivered",
  eyebrow = "Operations",
  title = "Internal Logistics & Operations Tracker",
  subtitle = "Final record of delivered orders — permanent archive for reporting & claims.",
  exportName = "ILOT",
} = {}) {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [billFilter, setBillFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const PAGE_SIZE = 20;
  const [page, setPage] = useState(1);
  const [noteFor, setNoteFor] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [noteStatus, setNoteStatus] = useState("open");
  const [notePhotos, setNotePhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [noteAmount, setNoteAmount] = useState("");
  const [claimFilter, setClaimFilter] = useState(false);

  const [vendors, setVendors] = useState([]);
  const [openId, setOpenId] = useState(null);
  const load = () => api.get(`/ilot?stage=${stage}`).then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  useEffect(() => { load(); api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {}); }, []);

  const assignLrVendor = async (leadId, lr, vendorId) => {
    const v = vendors.find((x) => x.id === vendorId);
    setRows((rs) => rs.map((r) => (r.id === leadId ? { ...r, lrs: (r.lrs || []).map((x) => (x.id === lr.id ? { ...x, vendor_id: vendorId, vendor_name: v ? v.name : "" } : x)) } : r)));
    try { await api.patch(`/leads/${leadId}/lorry-receipt/${lr.id}/vendor`, { vendor_id: vendorId, vendor_name: v ? v.name : "" }); toast.success("Truck vendor updated"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const isBilled = (r) => (r.lrs || []).some((lr) => lr.inv_status && lr.inv_status !== "unbilled");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const fromTs = from ? new Date(from + "T00:00:00").getTime() : null;
    const toTs = to ? new Date(to + "T23:59:59").getTime() : null;
    return rows.filter((r) => {
      if (q && !(r.lr_number || "").toLowerCase().includes(q) && !(r.party_name || "").toLowerCase().includes(q)) return false;
      if (fromTs || toTs) {
        const t = r.date ? new Date(r.date).getTime() : null;
        if (t == null) return false;
        if (fromTs && t < fromTs) return false;
        if (toTs && t > toTs) return false;
      }
      if (billFilter === "billed" && !isBilled(r)) return false;
      if (billFilter === "unbilled" && isBilled(r)) return false;
      if (claimFilter && !hasClaim(r)) return false;
      return true;
    });
  }, [rows, query, from, to, claimFilter, billFilter]);

  const thisMonth = () => {
    const d = new Date();
    const first = new Date(d.getFullYear(), d.getMonth(), 1);
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    const iso = (x) => x.toISOString().slice(0, 10);
    setFrom(iso(first)); setTo(iso(last));
  };
  const clearDates = () => { setFrom(""); setTo(""); };

  const rowCost = (r) => (Number(r.hhg_costing) || 0) + (Number(r.car_amount) || 0) + (Number(r.damage_amount) || 0);
  const rowMargin = (r) => (Number(r.quote_total) || 0) - rowCost(r);

  const totals = useMemo(() => filtered.reduce((a, r) => ({
    value: a.value + (Number(r.value) || 0),
    hhg: a.hhg + (Number(r.hhg_costing) || 0),
    car: a.car + (Number(r.car_amount) || 0),
    claim: a.claim + (Number(r.damage_amount) || 0),
    quote: a.quote + (Number(r.quote_total) || 0),
    cost: a.cost + rowCost(r),
    margin: a.margin + rowMargin(r),
  }), { value: 0, hhg: 0, car: 0, claim: 0, quote: 0, cost: 0, margin: 0 }), [filtered]);

  const setLocal = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const saveOps = async (id, patch) => {
    try { await api.patch(`/leads/${id}/ops`, patch); } catch (e) { toast.error(apiErr(e)); }
  };
  const markDelivered = async (id) => {
    try { await api.post(`/leads/${id}/deliver`); toast.success("Marked delivered — moved to ILOT"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const openNote = (r) => { setNoteFor(r); setNoteText(r.damage_claim || ""); setNoteStatus(r.damage_status || "open"); setNotePhotos(r.damage_photos || []); setNoteAmount(Number(r.damage_amount) > 0 ? String(r.damage_amount) : ""); };
  const saveNote = async () => {
    if (!noteFor) return;
    const amt = Number(noteAmount) || 0;
    const filed = noteText.trim() || notePhotos.length || amt > 0;
    const patch = { damage_claim: noteText, damage_status: filed ? (noteStatus || "open") : "", damage_photos: notePhotos, damage_amount: amt };
    setLocal(noteFor.id, patch);
    await saveOps(noteFor.id, patch);
    toast.success("Damage claim saved");
    setNoteFor(null);
  };
  const fileUrl = (p) => `${API}/files/${p}?auth=${localStorage.getItem("arrow_token") || ""}`;
  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setNotePhotos((p) => [...p, data.path]);
      toast.success("Photo uploaded");
    } catch (err) { toast.error(apiErr(err)); } finally { setUploadingPhoto(false); e.target.value = ""; }
  };
  const removePhoto = (p) => setNotePhotos((a) => a.filter((x) => x !== p));

  useEffect(() => { setPage(1); }, [query, from, to, statusFilter, rows, claimFilter, billFilter]);
  const cardFiltered = filtered.filter((r) => statusFilter === "all" || r.status === statusFilter);
  const tablePaged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const cardPaged = cardFiltered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const exportCsv = () => {
    const dmgHeaders = stage === "packing" ? ["Remarks"] : ["Damage Claim", "Claim Status", "Claim Amount"];
    const headers = ["Order", "ASSIGN", "Status", "Assigned To", "LR No", "Date", "Party", "Company", "From", "To", "Contained", "Value", "Type", "Contact", "Load Type", "HHG Vendor", "HHG Costing", "Car Vendor", "Car Amount", "Quote Total", "Total Cost", "Margin", ...dmgHeaders];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [headers.join(",")];
    filtered.forEach((r) => {
      const dmgVals = stage === "packing" ? [r.remarks] : [r.damage_claim, r.damage_status, r.damage_amount];
      lines.push([r.order_number, r.assign, r.status, r.assigned_exec, r.lr_number, fmtDate(r.date), r.party_name, r.company, r.from_city, r.to_city, r.contained, r.value, r.goods_type, r.contact, r.load_type, r.hhg_vendor, r.hhg_costing, r.car_vendor, r.car_amount, r.quote_total, rowCost(r), rowMargin(r), ...dmgVals].map(esc).join(","));
    });
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${exportName}-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`${exportName} sheet exported`);
  };

  const stickyHead = "sticky top-0 z-20 bg-muted";
  const frozenHead = "sticky top-0 z-30 bg-muted";
  const cellBase = "px-3 py-2.5 align-top border-b border-border";
  const shadowCol = "border-r-2 border-r-border shadow-[2px_0_4px_rgba(0,0,0,0.15)]";

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-primary flex items-center gap-2"><Boxes className="h-4 w-4" /> {eyebrow}</div>
          <h1 className="font-heading font-black text-2xl sm:text-4xl tracking-tight">{title}</h1>
          <p className="text-muted-foreground text-sm">{subtitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="rounded-none pl-9 h-11 bg-card"
              placeholder="Search by LR Number or Party Name…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="ilot-search"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <Input type="date" className="rounded-none h-11 bg-card w-[150px]" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="ilot-from" title="From date" />
            <span className="text-muted-foreground text-xs">to</span>
            <Input type="date" className="rounded-none h-11 bg-card w-[150px]" value={to} onChange={(e) => setTo(e.target.value)} data-testid="ilot-to" title="To date" />
          </div>
          <Button variant="outline" className="rounded-none h-11 text-xs" onClick={thisMonth} data-testid="ilot-this-month">This Month</Button>
          {(from || to) && <Button variant="ghost" className="rounded-none h-11 text-xs" onClick={clearDates} data-testid="ilot-clear-dates">Clear</Button>}
          <Select value={billFilter} onValueChange={setBillFilter}>
            <SelectTrigger className="rounded-none h-11 w-[140px] bg-card text-xs" data-testid="ilot-bill-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Orders</SelectItem>
              <SelectItem value="billed">Billed</SelectItem>
              <SelectItem value="unbilled">Unbilled</SelectItem>
            </SelectContent>
          </Select>
          {stage !== "packing" && <Button variant={claimFilter ? "default" : "outline"} className={`rounded-none h-11 gap-2 text-xs font-bold uppercase tracking-wide ${claimFilter ? "bg-red-500 hover:bg-red-600 text-white" : "text-red-500 border-red-500/40 hover:bg-red-500/10"}`} onClick={() => setClaimFilter((v) => !v)} data-testid="ilot-claim-filter"><AlertTriangle className="h-4 w-4" /> {claimFilter ? "Showing Claims" : "Filter by Damage Claim"}</Button>}
          <Button className="rounded-none h-11 gap-2 font-bold uppercase tracking-wide" onClick={exportCsv} data-testid="ilot-export"><Download className="h-4 w-4" /> Export</Button>
        </div>
      </div>

      <div className="hidden lg:block border border-border bg-card overflow-x-auto" data-testid="ilot-table-wrap">
        <table className="w-full text-sm border-collapse min-w-[2020px]" data-testid="ilot-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className={`${frozenHead} left-0 ${cellBase} w-[140px] min-w-[140px]`}>ASSIGN</th>
              <th className={`${frozenHead} left-[140px] ${cellBase} w-[150px] min-w-[150px] ${shadowCol}`}>LR No. &amp; Date</th>
              <th className={`${stickyHead} ${cellBase} min-w-[160px] text-right`}>Cost &amp; Margin</th>
              <th className={`${stickyHead} ${cellBase} min-w-[200px]`}>Party Name &amp; Company</th>
              <th className={`${stickyHead} ${cellBase} min-w-[180px]`}>From &amp; To</th>
              <th className={`${stickyHead} ${cellBase} min-w-[240px]`}>Contained</th>
              <th className={`${stickyHead} ${cellBase} min-w-[140px]`}>Value &amp; Type</th>
              <th className={`${stickyHead} ${cellBase} min-w-[130px]`}>Contact</th>
              <th className={`${stickyHead} ${cellBase} min-w-[130px]`}>Load Type</th>
              <th className={`${stickyHead} ${cellBase} min-w-[180px]`}>HHG Vendor &amp; Costing</th>
              <th className={`${stickyHead} ${cellBase} min-w-[200px]`}>{stage === "packing" ? "Remarks" : "Damage Claims"}</th>
              {stage !== "packing" && <th className={`${stickyHead} ${cellBase} min-w-[130px] text-right`}>Claim Amount</th>}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={13} className="p-10 text-center text-muted-foreground">Loading tracker…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={13} className="p-10 text-center text-muted-foreground">No orders match your search.</td></tr>}
            {!loading && tablePaged.map((r) => (
              <tr key={r.id} className="hover:bg-muted/50 transition-colors group" data-testid={`ilot-row-${r.order_number}`}>
                <td className={`${cellBase} sticky left-0 z-10 bg-card group-hover:bg-muted/50 w-[140px]`}>
                  <button type="button" className="font-mono-num text-primary text-xs hover:underline text-left" onClick={() => setOpenId(r.id)} data-testid={`ilot-open-${r.order_number}`}>{r.order_number}</button>
                  {r.move_date && <div className="text-[10px] text-muted-foreground mt-0.5" title="Moving date">Moving: {r.move_date}</div>}
                  <div className="mt-1 flex items-center gap-1"><StatusPill status={r.status} /><StageBadge stage={stage} /></div>
                  {(r.lrs || []).length > 0 && (
                    <div className="mt-1 space-y-1" data-testid={`ilot-lrs-${r.order_number}`}>
                      {r.lrs.map((lr) => {
                        const paidLabel = lr.pay_status === "paid" ? "Paid" : lr.pay_status === "partial" ? "Partial" : "Not Paid";
                        const billed = lr.inv_status && lr.inv_status !== "unbilled";
                        return (
                        <div key={lr.id} className="text-[10px] border-b border-border/50 pb-1 last:border-0" data-testid={`ilot-lr-assign-${r.order_number}-${lr.suffix}`}>
                          <div className="flex items-center gap-1 flex-wrap">
                            <span className="font-mono-num font-bold text-muted-foreground">{lr.suffix}</span>
                            <span className="font-medium truncate max-w-[80px]" title={lr.vendor_name}>{lr.vendor_name || "—"}</span>
                            <span className="text-muted-foreground">|</span>
                            <span className="font-mono-num">₹{Number(lr.vendor_cost || 0).toLocaleString("en-IN")}</span>
                            <span className="text-muted-foreground">|</span>
                            <span className={`font-bold uppercase ${lr.pay_status === "paid" ? "text-emerald-600 dark:text-emerald-400" : lr.pay_status === "partial" ? "text-blue-600 dark:text-blue-400" : "text-red-600 dark:text-red-400"}`}>{paidLabel}</span>
                            <span className="text-muted-foreground">|</span>
                            <span className={`font-bold uppercase ${billed ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>{billed ? "Billed" : "Not Billed"}</span>
                          </div>
                          <select className="rounded-none h-5 w-full mt-0.5 text-[10px] bg-background border border-border px-0.5" value={lr.vendor_id || ""} onChange={(e) => assignLrVendor(r.id, lr, e.target.value)} data-testid={`ilot-lr-vendor-${r.order_number}-${lr.suffix}`} onClick={(e) => e.stopPropagation()}>
                            <option value="">— assign vendor —</option>
                            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                          </select>
                        </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="text-[11px] text-muted-foreground mt-1 truncate" title={r.assigned_exec}>{r.assigned_exec}</div>
                  <Input
                    className="rounded-none h-7 mt-1.5 text-xs bg-background"
                    value={r.assign || ""}
                    placeholder="ASSIGN…"
                    onChange={(e) => setLocal(r.id, { assign: e.target.value })}
                    onBlur={() => saveOps(r.id, { assign: r.assign || "" })}
                    data-testid={`ilot-assign-${r.order_number}`}
                  />
                  {stage === "packing" && <button onClick={() => markDelivered(r.id)} className="mt-1.5 w-full inline-flex items-center justify-center gap-1 h-7 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wide hover:bg-emerald-700 transition-colors" data-testid={`ilot-deliver-${r.order_number}`}>Mark Delivered</button>}
                </td>
                <td className={`${cellBase} sticky left-[140px] z-10 bg-card group-hover:bg-muted/50 w-[150px] ${shadowCol}`} data-testid={`ilot-lr-${r.order_number}`}>
                  <div className="font-mono-num font-bold text-xs">{r.lr_number || "—"}</div>
                  <div className="text-[11px] text-muted-foreground">{fmtDate(r.date)}</div>
                </td>
                <td className={`${cellBase} text-right`} data-testid={`ilot-margin-${r.order_number}`}>
                  {(r.lrs || []).length > 1 ? (
                    <div className="space-y-0.5">
                      {r.lrs.map((lr) => <div key={lr.id} className="font-mono-num text-[11px]"><span className="text-muted-foreground">{lr.suffix}:</span> {money(lr.vendor_cost || 0)}</div>)}
                      <div className="font-mono-num text-xs border-t border-border pt-0.5">Cost: {money(rowCost(r))}</div>
                      <div className={`font-mono-num text-xs font-bold ${rowMargin(r) >= 0 ? "text-emerald-500" : "text-red-500"}`}>Margin: {money(rowMargin(r))}</div>
                    </div>
                  ) : (
                    <>
                      <div className="font-mono-num text-xs">Cost: {money(rowCost(r))}</div>
                      <div className={`font-mono-num text-xs font-bold ${rowMargin(r) >= 0 ? "text-emerald-500" : "text-red-500"}`}>Margin: {money(rowMargin(r))}</div>
                    </>
                  )}
                </td>
                <td className={cellBase}>
                  <div className="font-medium">{r.party_name || "—"}</div>
                  {r.company && <div className="text-xs text-muted-foreground">{r.company}</div>}
                </td>
                <td className={cellBase}>
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="font-medium">{r.from_city || "—"}</span>
                    <span className="text-primary">→</span>
                    <span className="font-medium">{r.to_city || "—"}</span>
                  </div>
                </td>
                <td className={`${cellBase} text-xs text-muted-foreground max-w-[240px]`}>
                  {(r.lrs || []).length > 1 ? (
                    <div className="space-y-0.5">
                      {r.lrs.map((lr) => <div key={lr.id}><span className="font-mono-num text-muted-foreground">{lr.suffix}:</span> {(lr.goods_type || "").replace("Transportation of ", "") || "—"}</div>)}
                    </div>
                  ) : r.contained}
                </td>
                <td className={cellBase}>
                  <div className="font-mono-num font-bold">{money(r.value)}</div>
                  {(r.lrs || []).length > 1 ? (
                    r.lrs.map((lr) => <div key={lr.id} className="text-[11px] uppercase text-muted-foreground"><span className="font-mono-num">{lr.suffix}:</span> {(lr.goods_type || "").replace("Transportation of ", "") || "—"}</div>)
                  ) : (
                    <div className="text-[11px] uppercase text-muted-foreground">{r.goods_type || "—"}</div>
                  )}
                </td>
                <td className={`${cellBase} font-mono-num text-xs`}>{r.contact || "—"}</td>
                <td className={cellBase}>
                  <select
                    className="rounded-none h-8 text-xs bg-background border border-border px-2 w-full font-bold uppercase"
                    value={r.load_type || ""}
                    onChange={(e) => { setLocal(r.id, { load_type: e.target.value }); saveOps(r.id, { load_type: e.target.value }); }}
                    data-testid={`ilot-loadtype-${r.order_number}`}
                  >
                    {LOAD_TYPES.map((lt) => <option key={lt || "none"} value={lt}>{lt || "—"}</option>)}
                  </select>
                </td>
                <td className={cellBase}>
                  <div className="text-xs font-medium">{r.hhg_vendor || "—"}</div>
                  <div className="font-mono-num text-primary text-xs">{r.hhg_costing != null ? money(r.hhg_costing) : "—"}</div>
                </td>
                {stage === "packing" ? (
                  <td className={cellBase} data-testid={`ilot-remarks-${r.order_number}`}>
                    <RemarksCell row={r} onSave={(v) => { setLocal(r.id, { remarks: v }); saveOps(r.id, { remarks: v }); }} />
                  </td>
                ) : (
                <td className={cellBase} data-testid={`ilot-damage-${r.order_number}`}>
                  {r.damage_claim || (r.damage_photos || []).length || Number(r.damage_amount) > 0 ? (
                    <>
                      <div className="flex items-center gap-1.5 mb-1">
                        <DamageBadge status={r.damage_status || "open"} />
                        {(r.damage_photos || []).length > 0 && <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><ImageIcon className="h-3 w-3" />{r.damage_photos.length}</span>}
                      </div>
                      {r.damage_claim && <div className="text-xs text-red-500 flex items-start gap-1"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /><span className="line-clamp-3">{r.damage_claim}</span></div>}
                    </>
                  ) : <span className="text-[11px] text-muted-foreground">No claims</span>}
                  <button onClick={() => openNote(r)} className="mt-1 block text-[11px] font-bold uppercase tracking-wide text-primary hover:underline" data-testid={`ilot-damage-note-${r.order_number}`}>{r.damage_claim || (r.damage_photos || []).length || Number(r.damage_amount) > 0 ? "Edit Note" : "Add Note"}</button>
                </td>
                )}
                {stage !== "packing" && (
                <td className={`${cellBase} text-right`} data-testid={`ilot-claim-amount-${r.order_number}`}>
                  {Number(r.damage_amount) > 0 ? <span className="font-mono-num text-red-500 font-bold">{money(r.damage_amount)}</span> : <span className="text-[11px] text-muted-foreground">—</span>}
                </td>
                )}
              </tr>
            ))}
          </tbody>
          {!loading && filtered.length > 0 && (
            <tfoot>
              <tr className="bg-muted font-bold text-xs" data-testid="ilot-totals-row">
                <td className={`${cellBase} sticky left-0 z-10 bg-muted w-[140px] uppercase tracking-wide`}>Totals</td>
                <td className={`${cellBase} sticky left-[140px] z-10 bg-muted w-[150px] ${shadowCol}`}>{filtered.length} orders</td>
                <td className={`${cellBase} text-right`}>
                  <div className="font-mono-num">Cost: {money(totals.cost)}</div>
                  <div className={`font-mono-num ${totals.margin >= 0 ? "text-emerald-500" : "text-red-500"}`}>Margin: {money(totals.margin)}</div>
                </td>
                <td className={cellBase} colSpan={3} />
                <td className={`${cellBase} font-mono-num`}>{money(totals.value)}</td>
                <td className={cellBase} colSpan={2} />
                <td className={`${cellBase} font-mono-num`}>HHG: {money(totals.hhg)}</td>
                <td className={cellBase} />
                {stage !== "packing" && <td className={`${cellBase} font-mono-num text-right text-red-500`}>{money(totals.claim)}</td>}
              </tr>
            </tfoot>
          )}
        </table>
        <div className="px-3 pb-3">
          <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onPage={setPage} testid="ilot-pagination" />
        </div>
      </div>
      {/* Mobile status filter chips */}
      <div className="lg:hidden flex gap-2 overflow-x-auto pb-3 -mx-4 px-4" data-testid="ilot-chips">
        {["all", "pending", "assigned", "confirmed", "done"].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            data-testid={`ilot-chip-${s}`}
            className={`shrink-0 px-3 py-1.5 text-xs font-bold uppercase tracking-wide border rounded-full transition-colors ${statusFilter === s ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border"}`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Mobile card view */}
      <div className="lg:hidden space-y-3" data-testid="ilot-cards">
        {loading && <div className="border border-border p-8 text-center text-muted-foreground">Loading tracker…</div>}
        {!loading && cardFiltered.length === 0 && <div className="border border-border p-8 text-center text-muted-foreground">No orders match your filters.</div>}
        {!loading && cardPaged.map((r) => (
          <div key={r.id} className="border border-border bg-card p-3" data-testid={`ilot-card-${r.order_number}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-mono-num text-primary text-sm font-bold"><button type="button" className="hover:underline" onClick={() => setOpenId(r.id)} data-testid={`ilot-card-open-${r.order_number}`}>{r.order_number}</button></div>
                <div className="text-[11px] text-muted-foreground">{r.lr_number || "—"} · {fmtDate(r.date)}</div>
                {r.move_date && <div className="text-[11px] text-primary font-medium">Moving: {r.move_date}</div>}
              </div>
              <div className="flex flex-col items-end gap-1"><StatusPill status={r.status} /><StageBadge stage={stage} /></div>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <div><span className="text-muted-foreground">Party:</span> <span className="font-medium">{r.party_name || "—"}</span></div>
              <div><span className="text-muted-foreground">Route:</span> {r.from_city || "—"} → {r.to_city || "—"}</div>
              <div><span className="text-muted-foreground">Value:</span> <span className="font-mono-num">{money(r.value)}</span> <span className="uppercase text-muted-foreground">{r.goods_type || ""}</span></div>
              <div><span className="text-muted-foreground">Contact:</span> <span className="font-mono-num">{r.contact || "—"}</span></div>
              <div className="col-span-2"><span className="text-muted-foreground">Contained:</span> {(r.lrs || []).length > 1 ? r.lrs.map((lr) => `${lr.suffix}: ${(lr.goods_type || "").replace("Transportation of ", "") || "—"}`).join("  ·  ") : (r.contained || "—")}</div>
              <div className="col-span-2"><span className="text-muted-foreground">HHG:</span> {r.hhg_vendor || "—"} {r.hhg_costing != null ? `(${money(r.hhg_costing)})` : ""}</div>
            </div>
            {(r.lrs || []).length > 0 && (
              <div className="mt-2 border-t border-border pt-2 space-y-1.5">
                {r.lrs.map((lr) => {
                  const paidLabel = lr.pay_status === "paid" ? "Paid" : lr.pay_status === "partial" ? "Partial" : "Not Paid";
                  const billed = lr.inv_status && lr.inv_status !== "unbilled";
                  return (
                  <div key={lr.id} className="text-[11px] space-y-1" data-testid={`ilot-card-lr-${r.order_number}-${lr.suffix}`}>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-mono-num font-bold text-muted-foreground">{lr.suffix}</span>
                      <span className="font-medium">{lr.vendor_name || "—"}</span>
                      <span className="text-muted-foreground">|</span>
                      <span className="font-mono-num">₹{Number(lr.vendor_cost || 0).toLocaleString("en-IN")}</span>
                      <span className="text-muted-foreground">|</span>
                      <span className={`font-bold uppercase ${lr.pay_status === "paid" ? "text-emerald-600 dark:text-emerald-400" : lr.pay_status === "partial" ? "text-blue-600 dark:text-blue-400" : "text-red-600 dark:text-red-400"}`}>{paidLabel}</span>
                      <span className="text-muted-foreground">|</span>
                      <span className={`font-bold uppercase ${billed ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>{billed ? "Billed" : "Not Billed"}</span>
                    </div>
                    <select className="rounded-none h-7 w-full text-[11px] bg-background border border-border px-1" value={lr.vendor_id || ""} onChange={(e) => assignLrVendor(r.id, lr, e.target.value)} data-testid={`ilot-card-lr-vendor-${r.order_number}-${lr.suffix}`}>
                      <option value="">— assign vendor —</option>
                      {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </select>
                  </div>
                  );
                })}
              </div>
            )}
            <div className="mt-2 flex items-center justify-between text-xs border-t border-border pt-2">
              <span className="font-mono-num">Cost: {money(rowCost(r))}</span>
              <span className={`font-mono-num font-bold ${rowMargin(r) >= 0 ? "text-emerald-500" : "text-red-500"}`}>Margin: {money(rowMargin(r))}</span>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Input className="rounded-none h-9 text-xs bg-background" value={r.assign || ""} placeholder="ASSIGN…" onChange={(e) => setLocal(r.id, { assign: e.target.value })} onBlur={() => saveOps(r.id, { assign: r.assign || "" })} data-testid={`ilot-card-assign-${r.order_number}`} />
              <select className="rounded-none h-9 text-xs bg-background border border-border px-2 font-bold uppercase" value={r.load_type || ""} onChange={(e) => { setLocal(r.id, { load_type: e.target.value }); saveOps(r.id, { load_type: e.target.value }); }} data-testid={`ilot-card-loadtype-${r.order_number}`}>
                {LOAD_TYPES.map((lt) => <option key={lt || "none"} value={lt}>{lt || "—"}</option>)}
              </select>
            </div>
            <div className="mt-2 border-t border-border pt-2">
              {r.damage_claim || (r.damage_photos || []).length || Number(r.damage_amount) > 0 ? (
                <>
                  <div className="flex items-center gap-1.5 mb-1">
                    <DamageBadge status={r.damage_status || "open"} />
                    {(r.damage_photos || []).length > 0 && <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><ImageIcon className="h-3 w-3" />{r.damage_photos.length}</span>}
                    {Number(r.damage_amount) > 0 && <span className="text-[11px] font-mono-num text-red-500 font-bold ml-auto">{money(r.damage_amount)}</span>}
                  </div>
                  {r.damage_claim && <div className="text-xs text-red-500 flex items-start gap-1"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /><span>{r.damage_claim}</span></div>}
                </>
              ) : <span className="text-[11px] text-muted-foreground">No damage claims</span>}
              <button onClick={() => openNote(r)} className="mt-1 block text-[11px] font-bold uppercase tracking-wide text-primary" data-testid={`ilot-card-damage-note-${r.order_number}`}>{r.damage_claim || (r.damage_photos || []).length || Number(r.damage_amount) > 0 ? "Edit Note" : "Add Note"}</button>
            </div>
            {stage === "packing" && <button onClick={() => markDelivered(r.id)} className="mt-2 w-full inline-flex items-center justify-center gap-1 h-9 bg-emerald-600 text-white text-xs font-bold uppercase tracking-wide" data-testid={`ilot-card-deliver-${r.order_number}`}>Mark Delivered</button>}
          </div>
        ))}
        {!loading && <Pagination page={page} total={cardFiltered.length} pageSize={PAGE_SIZE} onPage={setPage} testid="ilot-pagination-mobile" />}
      </div>

      {!loading && <div className="mt-3 text-xs text-muted-foreground" data-testid="ilot-count">{filtered.length} order{filtered.length === 1 ? "" : "s"}</div>}

      <Dialog open={!!noteFor} onOpenChange={(o) => !o && setNoteFor(null)}>
        <DialogContent className="rounded-none max-w-lg" data-testid="damage-note-dialog">
          <DialogHeader><DialogTitle>Damage Claim — {noteFor?.order_number}</DialogTitle></DialogHeader>
          <div className="py-2 space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Claim Status</label>
              <Select value={noteStatus} onValueChange={setNoteStatus}>
                <SelectTrigger className="rounded-none mt-1" data-testid="damage-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DAMAGE_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Note</label>
              <Textarea rows={4} className="rounded-none mt-1" placeholder="Describe the reported damage or insurance claim…" value={noteText} onChange={(e) => setNoteText(e.target.value)} data-testid="damage-note-input" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Claim Amount (₹)</label>
              <Input type="number" min="0" step="0.01" className="rounded-none mt-1 font-mono-num" placeholder="0" value={noteAmount} onChange={(e) => setNoteAmount(e.target.value)} data-testid="damage-amount-input" />
              <p className="text-[11px] text-muted-foreground mt-1">Deducted from Net Profit for this order.</p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Photos</label>
              <div className="mt-1 flex flex-wrap gap-2">
                {notePhotos.map((p) => (
                  <div key={p} className="relative group" data-testid={`damage-photo-${notePhotos.indexOf(p)}`}>
                    <a href={fileUrl(p)} target="_blank" rel="noreferrer"><img src={fileUrl(p)} alt="damage" className="h-20 w-20 object-cover border border-border hover:border-primary" /></a>
                    <button onClick={() => removePhoto(p)} className="absolute -top-2 -right-2 h-5 w-5 grid place-items-center bg-destructive text-white rounded-full" data-testid={`damage-photo-remove-${notePhotos.indexOf(p)}`}><X className="h-3 w-3" /></button>
                  </div>
                ))}
                <label className={`h-20 w-20 border border-dashed border-border grid place-items-center cursor-pointer hover:border-primary transition-colors ${uploadingPhoto ? "opacity-50 pointer-events-none" : ""}`} data-testid="damage-photo-upload">
                  <span className="flex flex-col items-center text-[10px] text-muted-foreground gap-1"><Upload className="h-4 w-4" />{uploadingPhoto ? "…" : "Add"}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} disabled={uploadingPhoto} />
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            {(noteFor?.damage_claim || (noteFor?.damage_photos || []).length) ? <Button variant="ghost" className="rounded-none text-destructive mr-auto" onClick={() => { setNoteText(""); setNotePhotos([]); }} data-testid="damage-note-clear">Clear Claim</Button> : null}
            <Button variant="ghost" className="rounded-none" onClick={() => setNoteFor(null)}>Cancel</Button>
            <Button className="rounded-none" onClick={saveNote} data-testid="damage-note-save">Save Claim</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LeadDrawer leadId={openId} onClose={() => setOpenId(null)} onChange={load} />
    </div>
  );
}
